/* @flow */
import * as React from 'react';

import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';

import { translate } from 'react-i18next';

import AutoCompleteModel from 'models/forms/AutoCompleteModel.js';

import { getForm } from './setUpAccountsInviteUserForm';
import ElectroTextField from '../components/inputs/ElectroTextField/ElectroTextField';
import ElectroSelectExtra from '../components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import UserRadioGroup from './userRadioGroup';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import FormWrapper from '../../components/FormWrapper/FormWrapper';
import FormSection from '../../components/FormSection/FormSection';
import ElectroResponseMessage from '../components/inputs/ElectroResponseMessage';

import FormControls from '../components/controls/FormControls/FormControls';

import {
  autoCompleteTradingAccounts,
  autoCompleteAccountCompanies,
  autoCompleteCompanySites,
  autoCompleteBuildings,
  autoCompleteAccountUsers,
  autoCompleteCountries,
  autoCompleteUSStates,
} from 'api/forms.js';

import { createNewLocation, updateRegion, viewRegion } from 'permissions/account';
import { ENV } from '../../config';
import ElectroSelect from '../components/inputs/ElectroSelect/ElectroSelect';
import RequireForAccess, { ReactAccessConsumer } from 'react-access';

const actionsListData: any = [
  {
    label: 'general:actions.new_company',
    icon: '#icon-add',
    action: 'newCompany',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_site',
    icon: '#icon-add',
    action: 'newSite',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_building_transport',
    icon: '#icon-add',
    action: 'newBuilding',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_lab_room',
    icon: '#icon-add',
    action: 'newRoom',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_system_workspace',
    icon: '#icon-add',
    action: 'newSystem',
    permissions: createNewLocation,
  },
];

@translate()
@inject('actionDrawerStore', 'accountStore')
@observer
export default class AccountsInviteUserForm extends React.Component<*> {
  @observable defaultUserRole = '1';
  @observable defaultRadioValue = this.props.params.userRole || this.defaultUserRole;
  @observable accountsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteTradingAccounts);
  @observable locationCountry: AutoCompleteModel = new AutoCompleteModel(autoCompleteCountries);
  @observable locationState: AutoCompleteModel = new AutoCompleteModel(autoCompleteUSStates);
  @observable companyModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAccountCompanies()
  );
  @observable sitesModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteCompanySites);
  @observable buildingsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);
  @observable roomsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);
  @observable systemsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);
  @observable usersModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteAccountUsers);

  @observable selectedCountry = '';
  @observable localState = {
    selectedValue: '',
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
  };

  @observable form = getForm(this.props.params.id, this.localState, this.props.t);

  handleLocationCountryOnChange = (model: Object, selectedValue: Object) => {
    model.setResult(selectedValue);
    this.selectedCountry = selectedValue.code;
    this.localState.selectedValue = selectedValue.name;
  };

  handleAssignOtherAccountsOnChange = (selectedValue: string) => {
    this.localState.selectedValue = selectedValue;
  };

  render(): React.Element<*> {
    const { t } = this.props;
    const { form, accountsModel, locationCountry, locationState, selectedCountry } = this;

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <ElectroResponseMessage localState={this.localState} />
          <FormWrapper form={form}>
            <FormSection form={form} section="userEmail">
              <ElectroTextField field={form.$('userEmail.emailAddress')} />
            </FormSection>
            <FormSection form={form} section="chooseAccount">
              <ElectroSelectExtra
                field={form.$('chooseAccount.tradingAccount')}
                form={form}
                formName="chooseAccount"
                labelKey={['name', 'account_no']}
                multi={false}
                searchable={false}
                disabled
                singleSelectedValue={this.localState.selectedValue}
                props={{
                  model: accountsModel,
                }}
              />
            </FormSection>
            <FormSection form={form} section="userDetails">
              <ElectroTextField field={form.$('userDetails.firstName')} />
              <ElectroTextField field={form.$('userDetails.lastName')} />
              <ElectroTextField field={form.$('userDetails.phoneNumber')} />
              <ElectroSelect
                field={form.$('userDetails.country')}
                model={locationCountry}
                onChange={(selectedValue: Object) => {
                  form.$('userDetails.country').value = selectedValue;
                  this.handleLocationCountryOnChange(locationCountry, selectedValue);
                }}
              />
              {selectedCountry === 'US' && (
                <ElectroSelect
                  field={form.$('userDetails.state')}
                  model={locationState}
                  onChange={(selectedValue: Object) => {
                    form.$('userDetails.state').value = {
                      name: selectedValue.name,
                      abbreviation: selectedValue.code,
                    };
                  }}
                />
              )}
              {(selectedCountry === 'GB' || selectedCountry === 'CA') && (
                <ElectroTextField field={form.$('userDetails.county')} />
              )}
              <ElectroTextField field={form.$('userDetails.city')} />
              <ElectroTextField field={form.$('userDetails.postcode')} />
            </FormSection>
            <FormSection form={form} section="selectUserRole">
              <UserRadioGroup
                field={form.$('selectUserRole.role')}
                value={this.defaultRadioValue}
              />
            </FormSection>
            {/* ================| LOCATION |================ */}
            <FormSection form={form} section="assignOtherAccounts">
              <ElectroSelectExtra
                field={form.$('assignOtherAccounts.accounts')}
                form={form}
                formName="assignOtherAccounts"
                labelKey={['name', 'account_no']}
                exclude={[this.props.params.id]}
                multi={true}
                searchable={true}
                handleOnChange={this.handleAssignOtherAccountsOnChange}
                singleSelectedValue={this.localState.selectedValue}
                labelSeparator=" - "
                props={{
                  model: accountsModel,
                }}
              />
            </FormSection>

            <FormControls buttonText={t('general:actions.invite')} form={form} />
          </FormWrapper>
        </PageWrapper>
      </React.Fragment>
    );
  }
}
